import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, KeyRound, LogIn, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { api, auth } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";

const DEVICE_TOKEN_KEY = "device_trust_token";

export default function Login() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [isForgotStep, setIsForgotStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [demoLogin, setDemoLogin] = useState(null);

  useEffect(() => {
    api.getAuthConfig()
      .then((response) => {
        if (response.data.demo_mode && response.data.demo_email) {
          setDemoLogin({ email: response.data.demo_email, password: response.data.demo_password });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isOtpStep) {
        event.preventDefault();
        event.returnValue = "You will need to request a new verification code.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isOtpStep]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (isForgotStep) {
      if (!email) {
        toast.error("Please enter your email");
        return;
      }
      try {
        setLoading(true);
        const response = await api.forgotPassword(email);
        toast.success(response.data.message || "If your account is eligible, a password reset link will be sent.", { duration: 6000 });
        setIsForgotStep(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to process reset request");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!email || !password) {
      toast.error(t('enter_email_password') || "Please enter email and password");
      return;
    }

    try {
      setLoading(true);
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      const response = await api.login(email, password, deviceToken || undefined);

      if (response.data.requires_otp) {
        if (deviceToken) localStorage.removeItem(DEVICE_TOKEN_KEY);
        setIsOtpStep(true);
        toast.success(t('otp_sent_msg') || "A verification code has been sent to your email.");
        return;
      }

      auth.setAuth(response.data.token, response.data.user);
      toast.success(`${t('welcome') || 'Welcome'}, ${response.data.user.name}`);
      navigate("/");
    } catch (error) {
      console.error("Login failed:", error);
      toast.error(error.response?.data?.detail || t('invalid_credentials') || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error(t('enter_otp') || "Please enter the 6-digit verification code");
      return;
    }

    try {
      setLoading(true);
      const response = await api.verifyOtp(email, otpCode, rememberDevice);
      if (rememberDevice && response.data.device_token) {
        localStorage.setItem(DEVICE_TOKEN_KEY, response.data.device_token);
      }
      auth.setAuth(response.data.token, response.data.user);
      toast.success(`${t('welcome') || 'Welcome'}, ${response.data.user.name}`);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired verification code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md border-slate-200 rounded-sm shadow-lg animate-in fade-in zoom-in-95 duration-200">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center mx-auto mb-4">
            {isOtpStep ? <KeyRound className="w-6 h-6 text-white" /> : isForgotStep ? <Mail className="w-6 h-6 text-white" /> : <LogIn className="w-6 h-6 text-white" />}
          </div>
          <CardTitle className="text-2xl font-bold font-['Manrope']" data-testid="login-title">
            {isForgotStep ? "Reset Password" : t('app_title')}
          </CardTitle>
          <CardDescription>
            {isOtpStep ? (t('enter_otp') || "Enter the verification code sent to your email") : isForgotStep ? "Enter your email to request a reset link" : (t('login_subtitle') || 'Sign in to access the assessment system')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {!isOtpStep ? (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">{t('email_label') || 'Email'}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('email_placeholder') || 'Enter your email'} className="mt-1.5 h-11 rounded-sm border-slate-300" data-testid="email-input" autoComplete="email" disabled={loading} />
              </div>
              {!isForgotStep && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">{t('password_label') || 'Password'}</Label>
                    <button type="button" onClick={() => setIsForgotStep(true)} className="text-xs text-slate-500 hover:text-slate-700 focus:outline-none">Forgot password?</button>
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('password_placeholder') || 'Enter your password'} className="h-11 rounded-sm border-slate-300 pr-11" data-testid="password-input" autoComplete="current-password" disabled={loading} />
                    <button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} disabled={loading}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-slate-700 hover:bg-slate-800 rounded-sm h-11 mt-6" data-testid="login-btn">
                {loading ? (isForgotStep ? "Sending..." : (t('signing_in') || "Signing in...")) : isForgotStep ? "Send Reset Link" : (t('sign_in') || "Sign In")}
              </Button>
              {isForgotStep && <Button type="button" variant="ghost" onClick={() => setIsForgotStep(false)} disabled={loading} className="w-full text-slate-500 hover:text-slate-700 mt-2"><ArrowLeft className="w-4 h-4 mr-2" />{t('back_to_login') || "Back to Login"}</Button>}
              {demoLogin && !isForgotStep && (
                <div className="mt-6 rounded-sm border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="demo-credentials">
                  <div className="font-semibold text-slate-700">Demo account</div>
                  <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-slate-600">
                    <dt>Email</dt>
                    <dd className="font-mono text-slate-900 break-all">{demoLogin.email}</dd>
                    <dt>Password</dt>
                    <dd className="font-mono text-slate-900 break-all">{demoLogin.password}</dd>
                  </dl>
                  <Button type="button" variant="outline" onClick={() => { setEmail(demoLogin.email); setPassword(demoLogin.password); }} disabled={loading} className="mt-3 w-full rounded-sm h-9">
                    Use demo account
                  </Button>
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-4 animate-in slide-in-from-right-4 duration-300">
              <div>
                <Label htmlFor="otp" className="text-slate-700 flex justify-between"><span>{t('otp_code_label') || 'Verification Code'}</span><span className="text-slate-500 font-normal text-xs">{email}</span></Label>
                <Input id="otp" type="text" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))} placeholder={t('otp_code_placeholder') || 'Enter 6-digit code'} className="mt-1.5 h-12 text-center text-2xl tracking-[0.5em] font-mono rounded-sm border-slate-300" autoComplete="one-time-code" disabled={loading} autoFocus />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox id="remember-device" checked={rememberDevice} onCheckedChange={(checked) => setRememberDevice(!!checked)} disabled={loading} />
                <Label htmlFor="remember-device" className="text-sm text-slate-600 cursor-pointer font-normal">Remember this device for 14 days</Label>
              </div>
              <Button type="submit" disabled={loading || otpCode.length !== 6} className="w-full bg-slate-700 hover:bg-slate-800 rounded-sm h-11 mt-6">{loading ? (t('verifying_otp') || "Verifying...") : (t('verify_otp') || "Verify Code")}</Button>
              <Button type="button" variant="ghost" onClick={() => { setIsOtpStep(false); setOtpCode(""); setRememberDevice(false); }} disabled={loading} className="w-full text-slate-500 hover:text-slate-700 mt-2"><ArrowLeft className="w-4 h-4 mr-2" />{t('back_to_login') || "Back to Login"}</Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
